/* eslint-disable @typescript-eslint/no-explicit-any */
import { NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Input,
  input,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
  viewChild,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms'

import { TranslatePipe } from '@ngx-translate/core'
import { sortBy } from 'lodash-es'
import { SelectItem } from 'primeng/api'
import { Button } from 'primeng/button'
import { IconField } from 'primeng/iconfield'
import { InputGroup } from 'primeng/inputgroup'
import { InputGroupAddon } from 'primeng/inputgroupaddon'
import { InputIcon } from 'primeng/inputicon'
import { InputText } from 'primeng/inputtext'
import { Overlay } from 'primeng/overlay'
import { Nullable } from 'primeng/ts-helpers'
import { fromEvent, Subscription, takeUntil } from 'rxjs'
import { createTextMaskInputElement } from 'text-mask-core'

import { COUNTRY_CODES, CountryCodes } from '@ti/contracts'
import { TextMaskRef } from '@ti/core/common/contracts'
import { DeviceService } from '@ti/core/common/services'
import { applyTextMask, injectDestroy$ } from '@ti/core/common/utils'
import { ChevronIconComponent } from '@ti/core/icons/chevron'
import { CloseIconComponent } from '@ti/core/icons/close'
import { SearchIconComponent } from '@ti/core/icons/search'

import { COUNTRY_SETTINGS, LanguageService, phoneNumberValidator } from '../../i18n'
import { BottomDialogComponent } from '../../layout/components/bottom-dialog/bottom-dialog.component'
import { FormControlClassesDirective } from '../../layout/directives'
import { scrollToCenter } from '../../layout/utils'
import { AutocompleteSheetComponent } from '../autocomplete/autocomplete-sheet.component'

const DEFAULT_COUNTRY_CODE = CountryCodes.UnitedStates

@Component({
  selector: 'app-phone-number-input',
  templateUrl: 'phone-number-input.component.html',
  styleUrls: ['phone-number-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    // Primeng
    InputGroup,
    InputGroupAddon,
    InputText,
    Overlay,
    Button,
    IconField,
    InputIcon,
    // Custom
    AutocompleteSheetComponent,
    BottomDialogComponent,
    ChevronIconComponent,
    CloseIconComponent,
    FormControlClassesDirective,
    FormsModule,
    NgTemplateOutlet,
    SearchIconComponent,
    TranslatePipe,
  ],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: PhoneNumberInputV2Component, multi: true },
  ],
})
export class PhoneNumberInputV2Component implements ControlValueAccessor {
  public readonly autocompleteEnabled = input<boolean>(false)
  public readonly autocompleteOptions = input<Array<any>>([])
  public readonly autocompleteOverlayTitle = input<string>('')

  public readonly appendTo = input<
    HTMLElement | ElementRef | TemplateRef<any> | string | null | undefined | any
  >('body')
  public readonly defaultCountryCode = input<Nullable<CountryCodes>>(undefined)
  public readonly pinnedCountryCodes = input<CountryCodes[]>([DEFAULT_COUNTRY_CODE])
  public readonly placeholderText = input<string | undefined>(undefined)
  public readonly height = input<number>(40)
  public readonly mobilePanelTitle = input<string>('')

  protected readonly deviceService = inject(DeviceService)
  protected readonly languageService = inject(LanguageService)
  protected readonly destroy$ = injectDestroy$()

  protected readonly _value = signal('')
  protected readonly _disabled = signal(false)
  protected readonly internalValue = signal('') // phone number without country prefix
  protected readonly selectedCountryCode = signal<CountryCodes | undefined>(undefined)
  protected readonly isCountryOverlayOpened = signal(false)
  protected readonly isAutocompleteOverlayOpened = signal(false)
  protected readonly isSheetOpened = signal(false)
  /** The country marked in the phone sheet until Apply. */
  protected readonly pendingCountryCode = signal<CountryCodes | undefined>(undefined)
  protected readonly searchCriteria = signal('')

  @ViewChild('inputRef')
  protected readonly inputRef?: ElementRef<HTMLInputElement>

  @ViewChild('countriesOverlay')
  protected readonly countriesOverlayRef?: Overlay

  @ViewChild('autocompleteOverlay')
  protected readonly autocompleteOverlayRef?: Overlay

  private readonly sheetScrollWrapper = viewChild<ElementRef<HTMLElement>>('sheetScrollWrapper')
  private readonly recentsSheet = viewChild(AutocompleteSheetComponent)
  private readonly recentsInput = viewChild<ElementRef<HTMLInputElement>>('recentsInput')

  /** The recent numbers sheet edits its own country and number until Apply. */
  protected readonly recentsCountryCode = signal<CountryCodes>(DEFAULT_COUNTRY_CODE)
  protected readonly recentsNumber = signal('')
  protected readonly recentsQuery = signal('')

  protected readonly PLACEHOLDER_CHAR = '_'
  protected readonly matchNotNumericRE = /\D/g

  protected onChanged?: (value: string) => void
  protected onTouched?: () => void
  protected textMaskRef?: TextMaskRef
  protected recentsMaskRef?: TextMaskRef
  protected subscriptions = new Set<Subscription>()

  protected readonly countryLabels = toSignal(
    this.languageService.massTranslate$(
      COUNTRY_CODES.reduce(
        (carry, code) => {
          carry[code] = `countries.${code}`
          return carry
        },
        {} as Record<CountryCodes, string>,
      ),
    ),
  )

  protected readonly currentPrefix = computed(() => {
    const countryCode = this.selectedCountryCode()
    if (!countryCode) {
      return '--'
    } else {
      return COUNTRY_SETTINGS[countryCode]?.phoneCountryCode
    }
  })

  protected readonly currentMask = computed(() => {
    const countryCode = this.selectedCountryCode()
    if (!countryCode) {
      return ''
    } else {
      return COUNTRY_SETTINGS[countryCode]?.phoneMask?.replace(/9/g, '_')
    }
  })

  protected readonly countryCodeOptions = computed<SelectItem[]>(() => {
    const labels = this.countryLabels()
    return sortBy(
      COUNTRY_CODES.map((code) => ({
        label: labels?.[code] ?? '',
        value: code,
        title: COUNTRY_SETTINGS[code]?.phoneCountryCode,
      })),
      (option) => option.label,
    )
  })

  // Only the phone sheet searches; the desktop overlay always gets the full list
  protected readonly filteredCountryOptions = computed(() => {
    const search = this.searchCriteria().trim().toLocaleLowerCase()
    if (!search) {
      return this.countryCodeOptions()
    }
    return this.countryCodeOptions().filter((option) =>
      [option.label, option.title, option.value].some((text) =>
        String(text ?? '')
          .toLocaleLowerCase()
          .includes(search),
      ),
    )
  })

  protected readonly pinnedCountryOptions = computed(() => {
    const pinned = new Set(this.pinnedCountryCodes())
    return this.filteredCountryOptions().filter((option) => pinned.has(option.value))
  })

  protected readonly unpinnedCountryOptions = computed(() => {
    const pinned = new Set(this.pinnedCountryCodes())
    return this.filteredCountryOptions().filter((option) => !pinned.has(option.value))
  })

  protected readonly filteredAutocompleteOptions = computed(() => {
    const value = this.internalValue()
    const options = this.autocompleteOptions()
    if (value.length && options.length) {
      const search = value.replace(/\D/g, '')
      return options.filter((option) => option.value.includes(search))
    }
    return options
  })

  protected readonly recentsPrefix = computed(
    () => COUNTRY_SETTINGS[this.recentsCountryCode()]?.phoneCountryCode ?? '',
  )

  protected readonly recentsMask = computed(
    () => COUNTRY_SETTINGS[this.recentsCountryCode()]?.phoneMask?.replace(/9/g, '_') ?? '',
  )

  protected readonly recentsOptions = computed(() => {
    const search = this.recentsQuery()
    const options = this.autocompleteOptions()
    return search ? options.filter((option) => option.value.includes(search)) : options
  })

  // An empty number clears the field; anything else has to be complete
  protected readonly recentsNumberValid = computed(
    () =>
      !this.stripNonNumeric(this.recentsNumber()) ||
      !phoneNumberValidator({
        value: `${this.recentsPrefix()} ${this.recentsNumber()}`,
      } as unknown as AbstractControl),
  )

  public constructor() {
    effect(() => {
      const el = this.recentsInput()?.nativeElement
      const mask = this.recentsMask()
      if (el) {
        untracked(() => this.initRecentsMask(el, mask))
      }
    })

    // Automatically
    effect(() => {
      const defaultCountryCode = this.defaultCountryCode()
      if (defaultCountryCode && !this.selectedCountryCode()) {
        this.selectedCountryCode.set(defaultCountryCode)
        this.scheduleTextMaskRefresh()
      }
    })
  }

  @Input()
  public set value(value: string) {
    this.writeValue(value)
  }

  public get value() {
    return this._value()
  }

  @Input()
  public set disabled(value: boolean) {
    this.setDisabledState(value)
  }

  @Input()
  public set countryCode(country: CountryCodes) {
    this.scheduleTextMaskRefresh()
    this.selectedCountryCode.set(country)
    this.writeValue(this.buildFormattedPhoneValue())
  }

  public get disabled() {
    return this._disabled()
  }

  public reset(): void {
    this._value.set('')
    this.internalValue.set('')
    this.selectedCountryCode.set(undefined)
    this.scheduleTextMaskRefresh()
  }

  public writeValue(value: string) {
    let internalValue = ''
    if (value) {
      value = this.stripNonNumeric(value)

      // Set country code based on input data
      const countryCode =
        this.detectCountryCode(value) ?? this.defaultCountryCode() ?? DEFAULT_COUNTRY_CODE
      const countrySettings = COUNTRY_SETTINGS[countryCode]

      const prefix = countrySettings.phoneCountryCode.replace(this.matchNotNumericRE, '')
      const phone = value.startsWith(prefix) ? value.replace(new RegExp(`^${prefix}`), '') : value

      if (countryCode !== this.selectedCountryCode()) {
        this.selectedCountryCode.set(countryCode)
        this.scheduleTextMaskRefresh()
      }

      internalValue = applyTextMask(this.currentMask(), phone, this.PLACEHOLDER_CHAR)

      // Check if some symbols were removed after applying text mask
      if (phone.length > internalValue.replace(/^D/g, '').length) {
        setTimeout(() => {
          this.onChanged?.(this.currentPrefix() + internalValue)
        }, 32)
      }
    }

    this._value.set(value)
    this.internalValue.set(internalValue)
    this.textMaskRef?.update(internalValue)
  }

  public setDisabledState(value: boolean) {
    this._disabled.set(value)
    if (value) {
      this.cancelSheet()
      this.recentsSheet()?.cancelValue()
    }
  }

  public registerOnChange(fn: (value: string) => void) {
    this.onChanged = fn
  }

  public registerOnTouched(fn: () => void) {
    this.onTouched = fn
  }

  protected openCountryOverlay() {
    if (this._disabled()) {
      return
    }
    if (this.useMobileSheet()) {
      this.openSheet()
      return
    }
    this.isCountryOverlayOpened.set(!this.isCountryOverlayOpened())
  }

  /** Phones use the bottom sheet; tablets and desktops keep the overlay. */
  protected useMobileSheet(): boolean {
    return this.deviceService.isMobile()
  }

  protected openSheet() {
    this.searchCriteria.set('')
    this.pendingCountryCode.set(this.selectedCountryCode())
    this.isSheetOpened.set(true)
  }

  protected pickCountry(option: SelectItem) {
    if (this.isSheetOpened()) {
      this.pendingCountryCode.set(option.value)
    } else {
      this.selectCountryOption(option)
    }
  }

  protected applySheet() {
    const pending = this.pendingCountryCode()
    const option = this.countryCodeOptions().find((option) => option.value === pending)
    this.cancelSheet()
    if (option) {
      this.selectCountryOption(option)
    }
  }

  protected cancelSheet() {
    this.isSheetOpened.set(false)
    this.searchCriteria.set('')
  }

  protected scrollSheetToSelectedOption() {
    const el = this.sheetScrollWrapper()?.nativeElement
    const selected = el?.querySelector('.option-item.selected')
    if (el && selected) scrollToCenter(el, selected)
  }

  protected selectCountryOption(option: SelectItem) {
    this.isCountryOverlayOpened.set(false)

    if (option.value !== this.selectedCountryCode()) {
      this.selectedCountryCode.set(option.value)
      this.scheduleTextMaskRefresh()

      this.writeValue(this.buildFormattedPhoneValue())
      this.onChanged?.(this.formatOutputPhoneNumber())
    }
  }

  protected scheduleTextMaskRefresh(delay = 32) {
    setTimeout(() => {
      const el = this.inputRef?.nativeElement
      if (el) {
        this.initInputMask(el, this.currentMask())
      }
    }, delay)
  }

  /** Phones type the number in the recent numbers sheet, with the country fixed to the field's. */
  protected useRecentsSheet(): boolean {
    return this.autocompleteEnabled() && this.useMobileSheet()
  }

  protected openRecentsSheet() {
    if (this._disabled()) return

    const digits = this.stripNonNumeric(this.formatOutputPhoneNumber())
    const selected = this.autocompleteOptions().find((option) => option.value === digits)
    this.recentsCountryCode.set(
      this.selectedCountryCode() ?? this.defaultCountryCode() ?? DEFAULT_COUNTRY_CODE,
    )
    this.setRecentsNumber(this.internalValue())
    this.recentsSheet()?.openMobilePanel(this.stripNonNumeric(this.internalValue()), selected)
  }

  protected onRecentsInput(input: HTMLInputElement) {
    this.recentsMaskRef?.update()
    this.recentsNumber.set(input.value)
    this.recentsSheet()?.setQuery(this.stripNonNumeric(input.value))
  }

  protected submitRecents(event: Event) {
    event.preventDefault()
    this.recentsSheet()?.submit()
  }

  /** A marked number may belong to another country, so the sheet's prefix follows it. */
  protected markRecentOption(option: any) {
    const value = this.stripNonNumeric(option.value)
    const countryCode = this.detectCountryCode(value) ?? this.recentsCountryCode()
    const prefix = this.stripNonNumeric(COUNTRY_SETTINGS[countryCode].phoneCountryCode)
    this.recentsCountryCode.set(countryCode)
    this.setRecentsNumber(value.startsWith(prefix) ? value.slice(prefix.length) : value)
  }

  protected commitRecentsNumber() {
    const countryCode = this.recentsCountryCode()
    if (countryCode !== this.selectedCountryCode()) {
      this.selectedCountryCode.set(countryCode)
      this.scheduleTextMaskRefresh()
    }

    const digits = this.stripNonNumeric(this.recentsNumber())
    const internalValue = digits
      ? applyTextMask(this.currentMask(), digits, this.PLACEHOLDER_CHAR)
      : ''
    this.internalValue.set(internalValue)
    this.textMaskRef?.update(internalValue)

    const outputValue = this.formatOutputPhoneNumber()
    this._value.set(outputValue)
    this.onTouched?.()
    this.onChanged?.(outputValue)
  }

  protected openAutocompleteOverlay() {
    if (!this._disabled() && this.autocompleteEnabled() && !this.useRecentsSheet()) {
      setTimeout(() => {
        this.isAutocompleteOverlayOpened.set(true)
      }, 256)
    }
  }

  protected selectAutocompleteOption(option: any) {
    this.isAutocompleteOverlayOpened.set(false)
    this.writeValue(option.value)
    this.onTouched?.()
    this.onChanged?.(this.formatOutputPhoneNumber())
  }

  protected scrollCountriesOverlayToSelectedOption() {
    const el =
      this.countriesOverlayRef?.overlayViewChild?.nativeElement?.querySelector('.scroll-wrapper')
    const selectedEl = el?.querySelector('.option-item.selected')
    if (selectedEl) {
      selectedEl.scrollIntoView({ behavior: 'instant', block: 'center' })
    }
  }

  protected detectCountryCode(phone: string): CountryCodes | undefined {
    phone = this.stripNonNumeric(phone)

    const targetEntry = Object.entries(COUNTRY_SETTINGS)
      .sort(([a], [b]) => {
        const isCodeAPinned = this.pinnedCountryCodes().includes(a as CountryCodes)
        const isCodeBPinned = this.pinnedCountryCodes().includes(b as CountryCodes)
        const isCodeADefault = a === this.defaultCountryCode()
        const isCodeBDefault = b === this.defaultCountryCode()

        if (isCodeADefault && !isCodeBDefault) return -1
        if (!isCodeADefault && isCodeBDefault) return 1
        if (isCodeAPinned && !isCodeBPinned) return -1
        if (!isCodeAPinned && isCodeBPinned) return 1
        return 0
      })
      .find(([, settings]) => phone.startsWith(this.stripNonNumeric(settings.phoneCountryCode)))

    return targetEntry?.[0] as CountryCodes | undefined
  }

  protected initInputMask(input: HTMLInputElement, mask: string) {
    this.textMaskRef = createTextMaskInputElement({
      inputElement: input,
      mask: mask.split('').map((char) => (char === this.PLACEHOLDER_CHAR ? /[0-9]/ : char)),
      placeholderChar: this.PLACEHOLDER_CHAR,
      guide: true,
    })

    this.textMaskRef?.update(this.internalValue())

    if (this.subscriptions.size > 0) {
      this.subscriptions.forEach((sub) => sub.unsubscribe())
      this.subscriptions.clear()
    }

    this.subscriptions.add(
      fromEvent<KeyboardEvent>(input, 'keydown')
        .pipe(takeUntil(this.destroy$))
        .subscribe((event) => {
          this.onTouched?.()
          if (event.key === 'Enter') {
            event.preventDefault()
            return input.blur()
          }
        }),
    )

    this.subscriptions.add(
      fromEvent(input, 'input')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.textMaskRef?.update()
          this.internalValue.set(input.value)
          const outputValue = this.formatOutputPhoneNumber()
          this._value.set(outputValue)
          this.onChanged?.(outputValue)
        }),
    )
  }

  private initRecentsMask(input: HTMLInputElement, mask: string) {
    this.recentsMaskRef = createTextMaskInputElement({
      inputElement: input,
      mask: mask.split('').map((char) => (char === this.PLACEHOLDER_CHAR ? /[0-9]/ : char)),
      placeholderChar: this.PLACEHOLDER_CHAR,
      guide: true,
    })
    this.recentsMaskRef?.update(this.recentsNumber())
  }

  private setRecentsNumber(value: string) {
    const digits = this.stripNonNumeric(value)
    const number = digits ? applyTextMask(this.recentsMask(), digits, this.PLACEHOLDER_CHAR) : ''
    this.recentsNumber.set(number)
    this.recentsMaskRef?.update(number)
  }

  protected stripNonNumeric(value: string) {
    return value.replace(this.matchNotNumericRE, '')
  }

  protected buildFormattedPhoneValue(): string {
    return `${this.currentPrefix()} ${this.internalValue()}`
  }

  protected formatOutputPhoneNumber(): string {
    const internalValue = this.internalValue()
    const digits = internalValue.replace(/\D/g, '')
    return digits.length !== 0 ? `${this.currentPrefix()} ${internalValue}` : ''
  }
}
